import { useState, useEffect, useCallback, useRef } from "react";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Copy, FileText, ShieldWarning, Fire } from "@phosphor-icons/react";
import TamperOverlay from "@/components/fraud/TamperOverlay";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CLASS_COLORS = {
  discharge_summary: "bg-purple-100 text-purple-800 border-purple-200",
  hospital_bill: "bg-blue-100 text-blue-800 border-blue-200",
  hospital_bill_summary: "bg-sky-100 text-sky-800 border-sky-200",
  hospital_bill_detailed: "bg-blue-100 text-blue-800 border-blue-200",
  pharmacy_bill: "bg-cyan-100 text-cyan-800 border-cyan-200",
  lab_report: "bg-green-100 text-green-800 border-green-200",
  prescription: "bg-yellow-100 text-yellow-800 border-yellow-200",
  id_proof: "bg-orange-100 text-orange-800 border-orange-200",
  cancelled_cheque: "bg-pink-100 text-pink-800 border-pink-200",
  pre_hospitalization_bill: "bg-indigo-100 text-indigo-800 border-indigo-200",
  post_hospitalization_bill: "bg-teal-100 text-teal-800 border-teal-200",
  other: "bg-gray-100 text-gray-800 border-gray-200"
};

export default function PdfViewer({ claimId, totalPages, pages, selectedPage, onSelectPage, isProcessed, fraud, showHeatmap, onToggleHeatmap }) {
  const fraudPages = fraud?.analysis?.status === "completed" ? fraud.analysis.pages || [] : [];
  const fraudAlerts = fraud?.analysis?.status === "completed" ? fraud.analysis.triggered_alerts || [] : [];
  const fraudPageOf = (n) => fraudPages.find(p => p.page_number === n);
  const alertCountOf = (n) => fraudAlerts.filter(a => a.page_no === n).length;
  const [pageImages, setPageImages] = useState({});
  const [loadingImages, setLoadingImages] = useState({});
  const [failedImages, setFailedImages] = useState({});
  const pageImagesRef = useRef({});
  const loadingImagesRef = useRef({});

  const loadPageImage = useCallback(async (pageNum) => {
    if (pageImagesRef.current[pageNum] || loadingImagesRef.current[pageNum]) return;
    loadingImagesRef.current[pageNum] = true;
    setLoadingImages(prev => ({ ...prev, [pageNum]: true }));
    try {
      const response = await fetch(`${API}/claims/${claimId}/pages/${pageNum}/image`);
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        pageImagesRef.current[pageNum] = url;
        setPageImages(prev => ({ ...prev, [pageNum]: url }));
        setFailedImages(prev => ({ ...prev, [pageNum]: false }));
      } else {
        setFailedImages(prev => ({ ...prev, [pageNum]: true }));
      }
    } catch {
      setFailedImages(prev => ({ ...prev, [pageNum]: true }));
    } finally {
      loadingImagesRef.current[pageNum] = false;
      setLoadingImages(prev => ({ ...prev, [pageNum]: false }));
    }
  }, [claimId]);

  // Load selected page image
  useEffect(() => {
    if (!claimId || totalPages === 0) return;
    loadPageImage(selectedPage);
  }, [claimId, selectedPage, totalPages, loadPageImage]);

  // Preload adjacent pages
  useEffect(() => {
    if (!claimId || totalPages === 0) return;
    [selectedPage - 1, selectedPage + 1]
      .filter(p => p >= 1 && p <= totalPages)
      .forEach(p => loadPageImage(p));
  }, [claimId, selectedPage, totalPages, loadPageImage]);

  const getPageData = (pageNum) => pages.find(p => p.page_number === pageNum);

  return (
    <div className="flex-1 flex overflow-hidden" data-testid="pdf-viewer">
      {/* Thumbnail sidebar */}
      <div className="w-20 flex-shrink-0 border-r border-gray-300 bg-gray-200 overflow-y-auto">
        <div className="p-1.5 space-y-1.5">
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(num => {
            const pd = getPageData(num);
            return (
              <button
                key={num}
                onClick={() => onSelectPage(num)}
                data-testid={`page-thumb-${num}`}
                className={`w-full aspect-[3/4] rounded border-2 flex flex-col items-center justify-center text-xs font-medium transition-all relative ${
                  selectedPage === num
                    ? "border-[#002FA7] bg-white shadow-sm"
                    : "border-transparent bg-white/60 hover:bg-white hover:border-gray-400"
                }`}
              >
                <span className="text-gray-600">{num}</span>
                {pd?.is_duplicate && (
                  <Copy size={10} weight="bold" className="absolute top-0.5 right-0.5 text-red-500" />
                )}
                {pd?.fraud_indicators?.is_suspicious && !pd?.is_duplicate && (
                  <ShieldWarning size={10} weight="fill" className="absolute top-0.5 right-0.5 text-red-600" />
                )}
                {alertCountOf(num) > 0 && (
                  <span className="absolute top-0.5 left-0.5 min-w-[14px] h-[14px] rounded-full bg-red-600 text-white text-[8px] font-bold flex items-center justify-center px-0.5" data-testid={`thumb-alert-count-${num}`}>
                    {alertCountOf(num)}
                  </span>
                )}
                {pd?.classification && pd.classification !== "other" && (
                  <div className="absolute bottom-0.5 left-0.5 right-0.5">
                    <div className={`rounded px-0.5 py-px text-[7px] leading-tight truncate font-medium ${CLASS_COLORS[pd.classification] || CLASS_COLORS.other}`}>
                      {pd.classification.replace(/_/g, " ").slice(0, 10)}
                    </div>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Main page view */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Page info bar */}
        {getPageData(selectedPage) && (
          <div className="flex-shrink-0 bg-white border-b border-gray-300 px-3 py-2 flex items-center gap-2">
            <span className="text-xs text-gray-500">Page {selectedPage}:</span>
            <Badge variant="outline" className={`text-xs ${CLASS_COLORS[getPageData(selectedPage)?.classification] || ""}`}>
              {getPageData(selectedPage)?.classification?.replace(/_/g, " ") || "Unclassified"}
            </Badge>
            {getPageData(selectedPage)?.is_duplicate && (
              <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200 gap-1">
                <Copy size={10} /> Duplicate of Page {getPageData(selectedPage)?.duplicate_of}
              </Badge>
            )}
            {getPageData(selectedPage)?.fraud_indicators?.is_suspicious && (
              <Badge variant="outline" className="text-xs bg-red-100 text-red-800 border-red-300 gap-1 font-bold">
                <ShieldWarning size={10} weight="fill" /> FRAUD SUSPECT
              </Badge>
            )}
            {getPageData(selectedPage)?.confidence > 0 && (
              <span className="text-xs text-gray-400">
                ({Math.round(getPageData(selectedPage).confidence * 100)}% conf)
              </span>
            )}
            {fraudPages.length > 0 && (
              <button
                onClick={onToggleHeatmap}
                className={`ml-auto inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded border transition-colors ${
                  showHeatmap ? "bg-orange-600 border-orange-600 text-white" : "bg-white border-gray-300 text-gray-600 hover:border-orange-400 hover:text-orange-700"
                }`}
                data-testid="viewer-heatmap-toggle"
              >
                <Fire size={12} weight={showHeatmap ? "fill" : "regular"} />
                {showHeatmap ? "Overlay ON" : "Tamper overlay"}
                {alertCountOf(selectedPage) > 0 && <span className={`ml-1 rounded-full px-1.5 ${showHeatmap ? "bg-white/30" : "bg-red-600 text-white"}`}>{alertCountOf(selectedPage)}</span>}
              </button>
            )}
          </div>
        )}

        {/* Image display */}
        <ScrollArea className="flex-1">
          <div className="p-4 flex items-start justify-center min-h-full">
            {loadingImages[selectedPage] ? (
              <Skeleton className="w-full max-w-[600px] aspect-[3/4]" />
            ) : showHeatmap && fraudPageOf(selectedPage) ? (
              <TamperOverlay claimId={claimId} pageNo={selectedPage} baseSrc={pageImages[selectedPage]} fraudPage={fraudPageOf(selectedPage)} alerts={fraudAlerts} />
            ) : pageImages[selectedPage] ? (
              <img
                src={pageImages[selectedPage]}
                alt={`Page ${selectedPage}`}
                className="max-w-full shadow-lg rounded border border-gray-300"
                data-testid={`page-image-${selectedPage}`}
              />
            ) : (
              <div className="w-full max-w-[600px] aspect-[3/4] bg-white rounded border border-gray-300 flex flex-col items-center justify-center shadow-sm">
                <FileText size={48} className="text-gray-300 mb-3" />
                <p className="text-sm text-gray-400">
                  {failedImages[selectedPage] ? "Failed to load page image" : "Loading page..."}
                </p>
                {failedImages[selectedPage] && (
                  <button
                    onClick={() => {
                      setFailedImages(prev => ({ ...prev, [selectedPage]: false }));
                      loadPageImage(selectedPage);
                    }}
                    className="mt-2 text-xs text-[#002FA7] hover:underline"
                  >
                    Retry
                  </button>
                )}
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Page navigation */}
        <div className="flex-shrink-0 bg-white border-t border-gray-300 px-3 py-2 flex items-center justify-center gap-4">
          <button
            onClick={() => onSelectPage(Math.max(1, selectedPage - 1))}
            disabled={selectedPage <= 1}
            className="text-sm text-gray-600 hover:text-gray-900 disabled:opacity-30 px-3 py-1 rounded hover:bg-gray-100 transition-colors"
            data-testid="prev-page-btn"
          >
            Previous
          </button>
          <span className="text-sm font-medium text-gray-700" data-testid="page-indicator">
            {selectedPage} / {totalPages}
          </span>
          <button
            onClick={() => onSelectPage(Math.min(totalPages, selectedPage + 1))}
            disabled={selectedPage >= totalPages}
            className="text-sm text-gray-600 hover:text-gray-900 disabled:opacity-30 px-3 py-1 rounded hover:bg-gray-100 transition-colors"
            data-testid="next-page-btn"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
